/**
 * Bilingual copy for the Solutions overview page.
 * `en` holds the exact original English strings; `km` mirrors them in Khmer.
 */
export const en = {
  kicker: 'Solutions',
  title: 'One system, every department',
  lead:
    'People, selling, buying, stock, the books, the till, and your web shop all work ' +
    'together — with the same customers, products, and approvals. Pick a journey below to explore.',
  journeysLabel: 'End-to-end journeys',
  modulesLabel: 'Modules in the suite',
  journeyLinkLabel: 'How the journey works',
  journeySolutions: [
    {
      title: 'Sales',
      description:
        'CRM quotations, sales orders, invoices, POS, and the online store share one item catalog and one customer record — then receipts and AR land in Revenues.',
    },
    {
      title: 'HR',
      description:
        'Recruitment, contracts, attendance, leave, payroll (tax, NSSF, seniority), performance, and self-service sit on one employee record — hire to retire.',
    },
    {
      title: 'Inventory',
      description:
        'Purchase orders, goods received, transfers, adjustments, and self-service withdrawals keep on-hand balances in sync with invoicing.',
    },
    {
      title: 'Accounting',
      description:
        'Chart of accounts and GL mappings let approved invoices, bills, and payments post the ledger — then income statement, balance sheet, and cash flow.',
    },
    {
      title: 'CRM',
      description:
        'Leads become customers and opportunities, quotations approve into sales orders, and the same customer account carries through billing and receipts.',
    },
    {
      title: 'Operations',
      description:
        'POS, the online store, branches, warehouses, and approval rules share one catalog and org map — so a till sale, a web order, and a clinic visit hit the same stock and books.',
    },
  ],
  ctaTitle: 'See it in action',
  ctaDescription:
    'Book a short walkthrough with our team and we will show you the parts of AKAS that fit your business.',
  ctaPrimaryLabel: 'Request a walkthrough',
  ctaSecondaryLabel: 'Browse the guides',
};

export type Copy = typeof en;

export const km: Copy = {
  kicker: 'ដំណោះស្រាយ',
  title: 'ប្រព័ន្ធតែមួយ សម្រាប់គ្រប់ផ្នែក',
  lead:
    'បុគ្គលិក ការលក់ ការទិញ ស្តុក គណនេយ្យ ការលក់តាមបញ្ជរ និងហាងលើអ៊ីនធឺណិត ធ្វើការជាមួយគ្នាក្នុងប្រព័ន្ធតែមួយ — ប្រើិទិន្នន័យអតិថិជន ទំនិញ និងដំណើរការអនុម័ត តែមួយដូចគ្នា។ សូមជ្រើសរើសដំណើរការណាមួយខាងក្រោមដើម្បីស្វែងយល់បន្ថែម។',
  journeysLabel: 'ដំណើរការពីដើមដល់ចុង',
  modulesLabel: 'ម៉ូឌុលក្នុងប្រព័ន្ធ',
  journeyLinkLabel: 'របៀបដែលដំណើរការនេះធ្វើការ',
  journeySolutions: [
    {
      title: 'ការលក់',
      description:
        'សម្រង់តម្លៃ បញ្ជាទិញពីអតិថិជន វិក្កយបត្រ ការលក់តាមបញ្ជរ និងហាងលើអ៊ីនធឺណិត ប្រើបញ្ជីទំនិញ និងព័ត៌មានអតិថិជនរួមតែមួយ។ បង្កាន់ដៃ និងគណនីត្រូវទទួល ក៏ចូលទៅផ្នែកគ្រប់គ្រងចំណូលដោយស្វ័យប្រវត្តិ។',
    },
    {
      title: 'ធនធានមនុស្ស',
      description:
        'ការជ្រើសរើសបុគ្គលិក កិច្ចសន្យា វត្តមាន ច្បាប់ឈប់សម្រាក ប្រាក់ខែ (ពន្ធ NSSF និងប្រាក់ឋានន្តរសក្តិ) ការវាយតម្លៃ និងសេវាកម្មដោយខ្លួនឯង — នៅលើកំណត់ត្រាបុគ្គលិកតែមួយ ពីពេលជ្រើសរើសបុគ្គលិករហូតដល់ចាកចេញ។',
    },
    {
      title: 'ស្តុក',
      description:
        'បញ្ជាទិញ ការទទួលទំនិញ ការផ្ទេរស្តុក ការកែតម្រូវស្តុក និងការដកស្តុកដោយខ្លួនឯង ជួយឱ្យចំនួនស្តុកនៅសល់ត្រូវនឹងតួលេខក្នុងរបាយការណ៍ជានិច្ច។',
    },
    {
      title: 'គណនេយ្យ',
      description:
        'បញ្ជីគណនី និងការផ្គូផ្គងសៀវភៅធំ ធ្វើឱ្យ វិក្កយបត្រអតិថិជន វិក្កយបត្រអ្នកផ្គត់ផ្គង់ និងការទូទាត់ដែលបានអនុម័ត ចូលសៀវភៅទិន្នានុប្បវត្តិដោយស្វ័យប្រវត្តិ — រួចរៀបចំរបាយការណ៍ហិរញ្ញវត្ថុ ដូចជារបាយការណ៍ចំណេញខាត តារាងតុល្យការណ៍ និងលំហូរសាច់ប្រាក់ ដោយស្វ័យប្រវត្តិ។',
    },
    {
      title: 'គ្រប់គ្រងអតិថិជន',
      description:
        'អតិថិជនសក្តានុពលក្លាយជាអតិថិជនពិត ឱកាសលក់ សម្រង់តម្លៃបម្លែងជាបញ្ជាទិញពីអតិថិជន ហើយគណនីអតិថិជនតែមួយបន្តប្រើរហូតដល់ការចេញវិក្កយបត្រ និងបង្កាន់ដៃទទួលប្រាក់។',
    },
    {
      title: 'ប្រតិបត្តិការ',
      description:
        'ការលក់តាមបញ្ជរ ហាងលក់លើអ៊ីនធឺណិត សាខា ឃ្លាំង និងច្បាប់នៃការអនុម័តផ្សេងៗ ប្រើបញ្ជីទំនិញ និងបទដ្ឋានអង្គភាពតែមួយ — ដូច្នេះការលក់ទាំងអស់ ចូលស្តុក និងបញ្ជីគណនេយ្យតែមួយ។',
    },
  ],
  ctaTitle: 'មើលប្រព័ន្ធនេះដំណើរការផ្ទាល់',
  ctaDescription:
    'កក់ពេលណាត់ជួបជាមួយក្រុមការងារយើង ហើយយើងនឹងបង្ហាញផ្នែកនានានៃប្រព័ន្ធរបស់យើង ដែលសាកសមនឹងអាជីវកម្មរបស់អ្នក។',
  ctaPrimaryLabel: 'សុំការបង្ហាញផ្ទាល់',
  ctaSecondaryLabel: 'មើលមគ្គុទ្ទេសក៍ទាំងអស់',
};
